import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Tag,
  Modal,
  InputNumber,
  Switch,
  Tooltip,
  Input,
  message,
  Skeleton,
  Alert,
  Badge,
  Spin,
} from "antd";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import {
  LeftOutlined,
  SaveOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import moment from "moment";
import axios from "axios";

const MODAL_Z = 99999;

const DEFAULT_CURRENCY = "INR";

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const safeInt = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const safeNumber = (v, fallback = null) => {
  if (v === "" || v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const buildMonthDays = (monthMoment) => {
  const start = monthMoment.clone().startOf("month");
  const end = monthMoment.clone().endOf("month");
  const days = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, "day")) {
    days.push(cursor.clone());
    cursor.add(1, "day");
  }
  return days;
};

// default map for a month: dateKey -> values
const buildDefaultInventoryMap = (days, totalRooms = 0) => {
  const map = {};
  days.forEach((d) => {
    const key = d.format("YYYY-MM-DD");
    map[key] = {
      allotment: Math.max(0, safeInt(totalRooms, 0)),
      stopSell: false,
      minStay: 1,
      cta: false, // closed_to_arrival
      ctd: false, // closed_to_departure
      rateCardId: null,
      overrideRate: null,
      currencyCode: DEFAULT_CURRENCY,
      notes: "",
    };
  });
  return map;
};

const normalizeRoomTypes = (raw) => {
  const arr = Array.isArray(raw) ? raw : [];
  return arr
    .map((rt) => ({
      id: Number(rt.room_type_id ?? rt.id),
      name: rt.type_name ?? rt.name ?? "Room Type",
      total_rooms: safeInt(rt.total_rooms, 0),
    }))
    .filter((x) => !!x.id);
};

const swalToast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3200,
  timerProgressBar: true,
});

const BeManualInventoryCalendar = () => {
  const navigate = useNavigate();
  const q = useQuery();
  const returnTo = q.get("return") || "/booking-engine/room-allocation";

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api/hms";
  const token = localStorage.getItem("token");

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: token ? `Bearer ${token}` : "" } }),
    [token]
  );

  const ensureAuth = () => {
    if (!token) {
      message.error("You are not logged in.");
      return false;
    }
    return true;
  };

  // UX states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [softInit, setSoftInit] = useState(false);
  const [dirty, setDirty] = useState(false);
  const lastServerSnapRef = useRef(null);

  // Data
  const [roomTypes, setRoomTypes] = useState([]);
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState(null);

  const businessDate = moment().startOf("day");
  const [month, setMonth] = useState(businessDate.clone().startOf("month"));

  // inventory: dateKey -> values (for the selected room type and month)
  const [inventory, setInventory] = useState({});

  const days = useMemo(() => buildMonthDays(month), [month]);

  // Modal state (edit a day)
  const [open, setOpen] = useState(false);
  const [editDateKey, setEditDateKey] = useState(null);

  const selectedRoomType = useMemo(
    () => roomTypes.find((r) => r.id === selectedRoomTypeId) || null,
    [roomTypes, selectedRoomTypeId]
  );

  const monthStart = useMemo(() => month.clone().startOf("month").format("YYYY-MM-DD"), [month]);
  const monthEnd = useMemo(() => month.clone().endOf("month").format("YYYY-MM-DD"), [month]);

  const openEdit = (dateKey) => {
    setEditDateKey(dateKey);
    setOpen(true);
  };

  const patchDay = (dateKey, patch) => {
    setInventory((prev) => ({
      ...prev,
      [dateKey]: {
        ...(prev[dateKey] || {}),
        ...patch,
      },
    }));
    setDirty(true);
  };

  const applyBulk = (fn) => {
    setInventory((prev) => {
      const next = { ...(prev || {}) };
      days.forEach((d) => {
        const k = d.format("YYYY-MM-DD");
        next[k] = fn(k, next[k] || {});
      });
      return next;
    });
    setDirty(true);
  };

  const validateBeforeSave = () => {
    if (!selectedRoomTypeId) {
      message.error("Please select a room type.");
      return false;
    }

    // sanity checks
    for (const d of days) {
      const k = d.format("YYYY-MM-DD");
      const v = inventory[k] || {};
      const allotment = Math.max(0, safeInt(v.allotment, 0));
      const minStay = Math.max(1, safeInt(v.minStay, 1));
      if (minStay > 30) {
        message.error(`Min stay too high on ${k}.`);
        return false;
      }
      if (allotment > 999) {
        message.error(`Allotment too high on ${k}.`);
        return false;
      }
    }

    return true;
  };

  const buildSavePayload = () => {
    const items = days.map((d) => {
      const key = d.format("YYYY-MM-DD");
      const v = inventory[key] || {};

      return {
        be_room_type_id: Number(selectedRoomTypeId),
        inventory_date: key, // ✅ backend expects inventory_date
        allotment: Math.max(0, safeInt(v.allotment, 0)),
        stop_sell: !!v.stopSell,
        min_stay: Math.max(1, safeInt(v.minStay, 1)),
        closed_to_arrival: !!v.cta,
        closed_to_departure: !!v.ctd,
        rate_card_id: v.rateCardId != null && v.rateCardId !== "" ? Number(v.rateCardId) : null,
        override_rate: v.overrideRate != null && v.overrideRate !== "" ? Number(v.overrideRate) : null,
        currency_code: String(v.currencyCode || DEFAULT_CURRENCY).trim() || DEFAULT_CURRENCY,
        notes: String(v.notes || "").slice(0, 500),
      };
    });

    return { items };
  };

  const fetchRoomTypes = async () => {
    const res = await axios.get(`${apiUrl}/roomtype/listings/`, authHeaders);
    const rtRaw = res?.data?.roomTypes || res?.data?.data || res?.data || [];
    const normalized = normalizeRoomTypes(rtRaw);
    return normalized;
  };

  const fetchMonthCalendar = async (roomTypeId, startDate, endDate) => {
    const res = await axios.get(`${apiUrl}/booking-engine-settings/manual-calendar`, {
      ...authHeaders,
      params: { roomTypeId, startDate, endDate },
    });

    const rows = res?.data?.data || [];
    return Array.isArray(rows) ? rows : [];
  };

  const hydrateMonth = async ({ roomTypeId, monthMoment }) => {
    const m = monthMoment.clone().startOf("month");
    const startDate = m.clone().startOf("month").format("YYYY-MM-DD");
    const endDate = m.clone().endOf("month").format("YYYY-MM-DD");
    const monthDays = buildMonthDays(m);

    // base defaults: allotment defaults to total_rooms if available
    const rt = roomTypes.find((x) => x.id === roomTypeId);
    const defaults = buildDefaultInventoryMap(monthDays, rt?.total_rooms || 0);

    try {
      const rows = await fetchMonthCalendar(roomTypeId, startDate, endDate);

      if (!rows.length) {
        setSoftInit(true);
        setInventory(defaults);
        lastServerSnapRef.current = { month: m.format("YYYY-MM"), roomTypeId, inventory: defaults };
        setDirty(false);
        return;
      }

      // map DB rows -> UI map
      const next = { ...defaults };

      rows.forEach((r) => {
        const key = String(r.inventory_date).slice(0, 10);
        if (!next[key]) return;

        next[key] = {
          allotment: Math.max(0, safeInt(r.allotment, next[key].allotment)),
          stopSell: !!r.stop_sell,
          minStay: Math.max(1, safeInt(r.min_stay, 1)),
          cta: !!r.closed_to_arrival,
          ctd: !!r.closed_to_departure,
          rateCardId: r.rate_card_id != null ? Number(r.rate_card_id) : null,
          overrideRate: r.override_rate != null ? Number(r.override_rate) : null,
          currencyCode: String(r.currency_code || DEFAULT_CURRENCY),
          notes: String(r.notes || ""),
        };
      });

      setSoftInit(false);
      setInventory(next);
      lastServerSnapRef.current = { month: m.format("YYYY-MM"), roomTypeId, inventory: next };
      setDirty(false);
    } catch (err) {
      // enterprise UX: don’t block — show defaults + warning
      console.error(err);
      setSoftInit(true);
      setInventory(defaults);
      lastServerSnapRef.current = { month: m.format("YYYY-MM"), roomTypeId, inventory: defaults };
      setDirty(false);

      swalToast.fire({
        icon: "warning",
        title: "Showing safe defaults (calendar not loaded)",
      });
    }
  };

  const fetchAll = async () => {
    if (!ensureAuth()) return;
    setLoading(true);

    try {
      const rt = await fetchRoomTypes();
      if (!rt.length) {
        await Swal.fire({
          icon: "warning",
          title: "No room types found",
          text: "Create room types first, then return to manage manual inventory.",
          confirmButtonText: "OK",
        });
        setRoomTypes([]);
        setSelectedRoomTypeId(null);
        setInventory({});
        setSoftInit(true);
        return;
      }

      setRoomTypes(rt);

      const firstId = selectedRoomTypeId || rt[0].id;
      setSelectedRoomTypeId(firstId);

      await hydrateMonth({ roomTypeId: firstId, monthMoment: month });
    } catch (err) {
      console.error(err);
      await Swal.fire({
        icon: "error",
        title: "Unable to load manual calendar",
        text: err?.response?.data?.message || "Please try again.",
        confirmButtonText: "OK",
      });

      setSoftInit(true);
      setRoomTypes([]);
      setSelectedRoomTypeId(null);
      setInventory({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // when month changes (and we have a selected room type), reload month data
  useEffect(() => {
    if (!selectedRoomTypeId) return;
    if (!roomTypes.length) return;

    // if user has unsaved changes, don’t auto-nuke them silently
    const run = async () => {
      if (!dirty) {
        await hydrateMonth({ roomTypeId: selectedRoomTypeId, monthMoment: month });
        return;
      }

      const result = await Swal.fire({
        icon: "warning",
        title: "You have unsaved changes",
        text: "Switching month will discard changes for the current month unless you save first.",
        showCancelButton: true,
        confirmButtonText: "Discard & switch",
        cancelButtonText: "Stay here",
      });

      if (result.isConfirmed) {
        setDirty(false);
        await hydrateMonth({ roomTypeId: selectedRoomTypeId, monthMoment: month });
      } else {
        // revert month UI
        const snap = lastServerSnapRef.current;
        if (snap?.month) {
          setMonth(moment(snap.month, "YYYY-MM").startOf("month"));
        }
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const onSelectRoomType = async (id) => {
    if (id === selectedRoomTypeId) return;

    if (dirty) {
      const result = await Swal.fire({
        icon: "warning",
        title: "You have unsaved changes",
        text: "Switching room type will discard changes unless you save first.",
        showCancelButton: true,
        confirmButtonText: "Discard & switch",
        cancelButtonText: "Stay here",
      });
      if (!result.isConfirmed) return;
    }

    setSelectedRoomTypeId(id);
    setDirty(false);
    setLoading(true);
    try {
      await hydrateMonth({ roomTypeId: id, monthMoment: month });
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = async () => {
    if (!dirty) {
      message.info("No changes to discard.");
      return;
    }
    const snap = lastServerSnapRef.current;
    if (!snap?.inventory) return;

    const result = await Swal.fire({
      icon: "warning",
      title: "Discard changes?",
      text: "Your unsaved changes will be lost.",
      showCancelButton: true,
      confirmButtonText: "Discard",
      cancelButtonText: "Keep editing",
    });

    if (result.isConfirmed) {
      setInventory({ ...(snap.inventory || {}) });
      setDirty(false);
      message.success("Changes discarded.");
    }
  };

  const handleSave = async () => {
    if (!ensureAuth()) return;
    if (!validateBeforeSave()) return;

    setSaving(true);
    try {
      const payload = buildSavePayload();

      await axios.post(
        `${apiUrl}/booking-engine-settings/manual-calendar`, // ✅ correct route
        payload,
        authHeaders
      );

      message.success("Manual calendar saved.");
      setSoftInit(false);
      setDirty(false);
      lastServerSnapRef.current = {
        month: month.format("YYYY-MM"),
        roomTypeId: selectedRoomTypeId,
        inventory: { ...inventory },
      };
    } catch (err) {
      console.error(err);
      await Swal.fire({
        icon: "error",
        title: "Save failed",
        text: err?.response?.data?.message || "We couldn’t save the manual calendar. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteDayOverride = async (dateKey) => {
    if (!ensureAuth()) return;
    if (!selectedRoomTypeId) return;

    const result = await Swal.fire({
      icon: "warning",
      title: "Delete this day override?",
      text: "This will remove this record from the manual calendar table. (Default behavior may apply depending on your engine rules.)",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      setSaving(true);
      await axios.delete(`${apiUrl}/booking-engine-settings/manual-calendar`, {
        ...authHeaders,
        data: {
          be_room_type_id: Number(selectedRoomTypeId),
          inventory_date: dateKey,
        },
      });

      // after delete, set UI back to default day
      const totalRooms = selectedRoomType?.total_rooms || 0;
      patchDay(dateKey, {
        allotment: Math.max(0, safeInt(totalRooms, 0)),
        stopSell: false,
        minStay: 1,
        cta: false,
        ctd: false,
        rateCardId: null,
        overrideRate: null,
        currencyCode: DEFAULT_CURRENCY,
        notes: "",
      });

      swalToast.fire({ icon: "success", title: "Day override deleted" });
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Failed to delete day override.");
    } finally {
      setSaving(false);
    }
  };

  // --- derived stats ---
  const monthSummary = useMemo(() => {
    const keys = days.map((d) => d.format("YYYY-MM-DD"));
    let totalAllot = 0;
    let stopSellCount = 0;
    let ctaCount = 0;
    let ctdCount = 0;

    keys.forEach((k) => {
      const v = inventory[k] || {};
      totalAllot += Math.max(0, safeInt(v.allotment, 0));
      if (v.stopSell) stopSellCount += 1;
      if (v.cta) ctaCount += 1;
      if (v.ctd) ctdCount += 1;
    });

    return {
      totalAllot,
      stopSellCount,
      ctaCount,
      ctdCount,
    };
  }, [days, inventory]);

  if (loading) {
    return (
      <div className="page-wrapper-full">
        <div className="content container-fluid">
          <Card className="shadow-sm" style={{ borderRadius: 12 }}>
            <Skeleton active paragraph={{ rows: 8 }} />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">
        {/* Sticky top bar */}
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1000,
            background: "#fff",
            padding: "12px 0",
            borderBottom: "1px solid #eef2f7",
          }}
        >
          <Row align="middle" justify="space-between" gutter={[12, 12]}>
            <Col>
              <Button icon={<LeftOutlined />} onClick={() => navigate(returnTo)}>
                Back to Room Allocation
              </Button>
              <Tag color="blue" style={{ marginLeft: 12 }}>
                Manual Mode: BE is Master
              </Tag>
              {dirty ? (
                <Tag color="orange" style={{ marginLeft: 8 }}>
                  Unsaved changes
                </Tag>
              ) : (
                <Tag color="green" style={{ marginLeft: 8 }}>
                  Up to date
                </Tag>
              )}
            </Col>

            <Col style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <Button icon={<ReloadOutlined />} onClick={fetchAll} disabled={saving}>
                Refresh
              </Button>

              <Button type="default" onClick={() => setMonth((m) => m.clone().subtract(1, "month"))} disabled={saving}>
                Prev
              </Button>
              <Button
                type="default"
                onClick={() => setMonth(businessDate.clone().startOf("month"))}
                disabled={saving}
              >
                This Month
              </Button>
              <Button type="default" onClick={() => setMonth((m) => m.clone().add(1, "month"))} disabled={saving}>
                Next
              </Button>

              <Button icon={<UndoOutlined />} onClick={handleDiscard} disabled={!dirty || saving}>
                Discard
              </Button>

              <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
                Save
              </Button>
            </Col>
          </Row>
        </div>

        {/* Title */}
        <div className="page-header" style={{ marginTop: 16 }}>
          <h3 style={{ marginBottom: 6 }}>
            <CalendarOutlined /> Manual Availability Calendar
          </h3>
          <p className="text-muted" style={{ marginBottom: 0 }}>
            Control allotment + restrictions per date when no PMS is connected. (Saves into{" "}
            <code>be.be_manual_inventory_calendar</code>)
          </p>
        </div>

        {softInit && (
          <Alert
            type="info"
            showIcon
            style={{ borderRadius: 12, marginBottom: 12 }}
            message="No saved manual calendar found for this month"
            description="We loaded smart defaults (based on total rooms). Update any dates and click Save."
          />
        )}

        <Row gutter={[16, 16]}>
          {/* Left panel */}
          <Col md={6} xs={24}>
            <Card style={{ borderRadius: 12 }} className="shadow-sm">
              <div style={{ fontWeight: 900, marginBottom: 10 }}>Room Type</div>

              <div style={{ display: "grid", gap: 10 }}>
                {roomTypes.map((rt) => (
                  <div
                    key={rt.id}
                    onClick={() => onSelectRoomType(rt.id)}
                    style={{
                      cursor: "pointer",
                      padding: "12px 12px",
                      borderRadius: 12,
                      border: rt.id === selectedRoomTypeId ? "2px solid #1677ff" : "1px solid #e5e7eb",
                      background: rt.id === selectedRoomTypeId ? "rgba(22,119,255,0.06)" : "#fff",
                    }}
                  >
                    <div style={{ fontWeight: 900 }}>{rt.name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                      Total rooms: <b>{rt.total_rooms || 0}</b> · Click to manage
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px dashed #e5e7eb" }}>
                <div style={{ fontWeight: 900, marginBottom: 6 }}>
                  Bulk Actions{" "}
                  <Tooltip title="Applies to all days of the selected month for this room type.">
                    <InfoCircleOutlined />
                  </Tooltip>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button
                    onClick={() => {
                      const totalRooms = selectedRoomType?.total_rooms || 0;
                      applyBulk((k, v) => ({
                        ...v,
                        allotment: Math.max(0, safeInt(totalRooms, 0)),
                        stopSell: false,
                        minStay: 1,
                        cta: false,
                        ctd: false,
                        rateCardId: null,
                        overrideRate: null,
                        currencyCode: DEFAULT_CURRENCY,
                        notes: "",
                      }));
                    }}
                    disabled={saving}
                  >
                    Reset Month Defaults
                  </Button>

                  <Button
                    onClick={() =>
                      applyBulk((k, v) => {
                        const day = moment(k).day();
                        const weekend = day === 0 || day === 6;
                        return { ...v, stopSell: weekend ? true : v.stopSell };
                      })
                    }
                    disabled={saving}
                  >
                    Stop-sell Weekends
                  </Button>

                  <Button
                    onClick={() =>
                      applyBulk((k, v) => {
                        const day = moment(k).day();
                        const weekend = day === 0 || day === 6;
                        return { ...v, allotment: weekend ? 5 : v.allotment };
                      })
                    }
                    disabled={saving}
                  >
                    Weekend allotment = 5
                  </Button>
                </div>

                <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px dashed #e5e7eb" }}>
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>Month snapshot</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    Total allotment (sum): <b>{monthSummary.totalAllot}</b>
                    <br />
                    Stop-sell days: <b>{monthSummary.stopSellCount}</b>
                    <br />
                    CTA days: <b>{monthSummary.ctaCount}</b> · CTD days: <b>{monthSummary.ctdCount}</b>
                  </div>
                </div>
              </div>
            </Card>
          </Col>

          {/* Calendar grid */}
          <Col md={18} xs={24}>
            <Card style={{ borderRadius: 12 }} className="shadow-sm">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 900, fontSize: 16 }}>{selectedRoomType?.name || "Room Type"}</div>
                  <div style={{ color: "#6b7280", fontSize: 12, marginTop: 2 }}>
                    Month: <b>{month.format("MMMM YYYY")}</b> · Range: <b>{monthStart}</b> → <b>{monthEnd}</b>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {saving ? <Badge status="processing" text="Saving…" /> : dirty ? <Badge status="warning" text="Unsaved" /> : <Badge status="success" text="Saved" />}
                  <Tag color="geekblue">Click any date to edit</Tag>
                </div>
              </div>

              <div
                style={{
                  marginTop: 14,
                  display: "grid",
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                {days.map((d) => {
                  const key = d.format("YYYY-MM-DD");
                  const cell = inventory[key] || {};
                  const isPast = d.isBefore(businessDate, "day");
                  const isWeekend = [0, 6].includes(d.day());

                  const bg = cell.stopSell
                    ? "rgba(255,77,79,0.06)"
                    : cell.cta || cell.ctd
                      ? "rgba(250,173,20,0.10)"
                      : isWeekend
                        ? "rgba(22,119,255,0.05)"
                        : "#fff";

                  return (
                    <div
                      key={key}
                      onClick={() => !isPast && openEdit(key)}
                      style={{
                        cursor: isPast ? "not-allowed" : "pointer",
                        opacity: isPast ? 0.55 : 1,
                        borderRadius: 14,
                        border: "1px solid #e5e7eb",
                        padding: 12,
                        minHeight: 112,
                        background: bg,
                        transition: "transform 120ms ease, box-shadow 120ms ease",
                      }}
                      onMouseEnter={(e) => {
                        if (isPast) return;
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 10px 22px rgba(15,23,42,0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                        <div style={{ fontWeight: 900 }}>{d.format("DD")}</div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>{d.format("ddd")}</div>
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12 }}>
                          <span style={{ color: "#6b7280" }}>Allotment:</span> <b>{safeInt(cell.allotment, 0)}</b>
                        </div>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          <span style={{ color: "#6b7280" }}>Min stay:</span> <b>{Math.max(1, safeInt(cell.minStay, 1))}</b>
                        </div>

                        <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {cell.stopSell ? <Tag color="red">STOP-SELL</Tag> : <Tag color="green">SELLING</Tag>}
                          {(cell.cta || cell.ctd) && <Tag color="gold">RESTRICTED</Tag>}
                          {cell.overrideRate != null && cell.overrideRate !== "" && <Tag color="purple">RATE OVERRIDE</Tag>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Edit modal */}
        <Modal
          open={open}
          onCancel={() => setOpen(false)}
          onOk={() => setOpen(false)}
          okText="Done"
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 900 }}>Edit Day</span>
              <Tag color="blue">{selectedRoomType?.name}</Tag>
              {editDateKey && <Tag color="geekblue">{moment(editDateKey).format("DD MMM YYYY (ddd)")}</Tag>}
            </div>
          }
          style={{ top: 30 }}
          zIndex={MODAL_Z}
          maskStyle={{ zIndex: MODAL_Z - 1 }}
          getContainer={() => document.body}
        >
          {editDateKey ? (
            <>
              <div style={{ display: "grid", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ fontWeight: 800 }}>Stop Sell</div>
                  <Switch checked={!!inventory[editDateKey]?.stopSell} onChange={(v) => patchDay(editDateKey, { stopSell: v })} />
                </div>

                <div>
                  <div style={{ fontWeight: 800, marginBottom: 6 }}>Allotment (rooms Booking Engine can sell)</div>
                  <InputNumber
                    min={0}
                    max={999}
                    style={{ width: "100%" }}
                    value={safeInt(inventory[editDateKey]?.allotment, 0)}
                    onChange={(v) => patchDay(editDateKey, { allotment: Math.max(0, safeInt(v, 0)) })}
                  />
                </div>

                <div>
                  <div style={{ fontWeight: 800, marginBottom: 6 }}>Minimum Stay (nights)</div>
                  <InputNumber
                    min={1}
                    max={30}
                    style={{ width: "100%" }}
                    value={Math.max(1, safeInt(inventory[editDateKey]?.minStay, 1))}
                    onChange={(v) => patchDay(editDateKey, { minStay: Math.max(1, safeInt(v, 1)) })}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 10, border: "1px solid #eef2f7", borderRadius: 12 }}>
                    <div style={{ fontWeight: 700 }}>Closed to Arrival</div>
                    <Switch checked={!!inventory[editDateKey]?.cta} onChange={(v) => patchDay(editDateKey, { cta: v })} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 10, border: "1px solid #eef2f7", borderRadius: 12 }}>
                    <div style={{ fontWeight: 700 }}>Closed to Departure</div>
                    <Switch checked={!!inventory[editDateKey]?.ctd} onChange={(v) => patchDay(editDateKey, { ctd: v })} />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 800, marginBottom: 6 }}>
                      Override Rate{" "}
                      <Tooltip title="Optional. If set, Booking Engine can use this price for this date (depends on your pricing engine rules).">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </div>
                    <InputNumber
                      min={0}
                      max={9999999}
                      style={{ width: "100%" }}
                      value={inventory[editDateKey]?.overrideRate ?? null}
                      onChange={(v) => patchDay(editDateKey, { overrideRate: safeNumber(v, null) })}
                      placeholder="Leave empty to use rate plan"
                    />
                  </div>

                  <div>
                    <div style={{ fontWeight: 800, marginBottom: 6 }}>Currency</div>
                    <Input
                      value={inventory[editDateKey]?.currencyCode || DEFAULT_CURRENCY}
                      onChange={(e) => patchDay(editDateKey, { currencyCode: (e.target.value || DEFAULT_CURRENCY).toUpperCase() })}
                      placeholder="INR"
                    />
                  </div>
                </div>

                <div>
                  <div style={{ fontWeight: 800, marginBottom: 6 }}>Notes (optional)</div>
                  <Input.TextArea
                    rows={3}
                    value={inventory[editDateKey]?.notes || ""}
                    onChange={(e) => patchDay(editDateKey, { notes: e.target.value })}
                    placeholder="Internal note (not visible to guests)"
                    maxLength={500}
                    showCount
                  />
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ color: "#6b7280", fontSize: 12 }}>
                    Saved using <code>(companyid, be_room_type_id, inventory_date)</code> upsert.
                  </div>

                  <Button danger onClick={() => deleteDayOverride(editDateKey)}>
                    Delete day override
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </Modal>

        {/* Global saving overlay */}
        {saving && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(255,255,255,0.35)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(4px)",
            }}
          >
            <Card style={{ borderRadius: 14 }}>
              <Spin />
              <div style={{ marginTop: 10, fontWeight: 900 }}>Saving manual calendar…</div>
              <div style={{ color: "#6b7280", marginTop: 4 }}>Please don’t close this tab.</div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default BeManualInventoryCalendar;
